import { Select, SelectProps, Box, Typography, FormControl } from '@mui/material';
import { forwardRef, ReactNode } from 'react';

/**
 * Reusable form select component that wraps Material-UI Select
 * with consistent styling matching FormTextField.
 *
 * Features:
 * - Consistent sizing and spacing with text inputs
 * - Mobile-optimized (16px font to prevent iOS auto-zoom)
 * - Minimum touch target height (50px)
 * - Standardized error and focus states
 * - Accessibility-ready with ARIA attributes
 * - Separate label and select styling
 */
export interface FormSelectProps extends Omit<SelectProps, 'variant'> {
  /**
   * Label text displayed above the select
   */
  label?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Error message to display below the select
   */
  helperText?: string;
  /**
   * Select options as MenuItem children
   */
  children: ReactNode;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    { label, required, error, helperText, sx, children, MenuProps: menuPropsPassed, ...props },
    ref
  ) => {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        {label && (
          <Typography
            component="label"
            htmlFor={props.id}
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: error ? 'error.main' : 'text.primary',
              mb: 1,
            }}
          >
            {label}
            {required && (
              <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <FormControl fullWidth error={error} sx={{ minWidth: 0 }}>
          <Select
            ref={ref as React.Ref<HTMLSelectElement>}
            displayEmpty
            aria-required={required || undefined}
            aria-invalid={error || undefined}
            MenuProps={{
              disableScrollLock: true,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: { sx: { mt: '2px' } },
              ...menuPropsPassed,
            }}
            sx={{
              borderRadius: '10px',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              height: '44px',
              minHeight: '44px',
              fontSize: { xs: '16px', sm: '14px' }, // Prevent iOS auto-zoom on mobile
              backgroundColor: 'background.paper',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'error.main' : '#E5E7EB',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'error.main' : '#D1D5DB',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'error.main' : 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
              },
              '& .MuiSelect-select': {
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                height: '44px',
                lineHeight: '28px',
                display: 'block',
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              ...sx,
            }}
            {...props}
          >
            {children}
          </Select>
          {helperText && (
            <Typography
              sx={{
                color: error ? 'error.main' : 'text.secondary',
                fontSize: '0.875rem',
                marginLeft: 0,
                marginTop: '6px',
                minHeight: '18px',
              }}
            >
              {helperText}
            </Typography>
          )}
        </FormControl>
      </Box>
    );
  }
);

FormSelect.displayName = 'FormSelect';
